/*
 * BrandingSmartFill — top-of-panel CTA cluster on Settings → Branding.
 *
 * Two paths to skip the manual upload grind:
 *
 *   1. "Import from your website" — paste a URL, we scrape it with
 *      httpx + BeautifulSoup on the backend, return candidate fields
 *      (company name, description, primary color, logo, favicon, cover
 *      image). The seller reviews each field via checkbox and clicks
 *      "Apply selected". Each chosen image is re-hosted on our object
 *      store so the seller's branding doesn't break when the source
 *      site moves. ~70% of sites yield a great result; the rest fall
 *      back to manual.
 *
 *   2. "Generate from name + colors" — server-side renders monogram +
 *      dark-mode logo + cover banner using PIL. Pure deterministic
 *      output, no AI cost. Used as a fallback when (1) fails or for
 *      sellers without a website.
 *
 * Modeled after Stripe Dashboard, Linear and Vercel onboarding flows.
 */
import { useState } from "react";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Sparkles, Globe, Wand2, Loader2, AlertTriangle, CheckCircle2,
  ArrowRight, X, Palette,
} from "lucide-react";
import { toast } from "sonner";

export default function BrandingSmartFill({ onChanged }) {
  const [importOpen, setImportOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/seller/branding/generate", {
        variants: ["logo_square", "logo_dark", "cover_image"],
      });
      const count = data?.count || 0;
      toast.success(`Generated ${count} branding ${count === 1 ? "asset" : "assets"}.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <div
        className="rounded-2xl border border-foreground/10 bg-gradient-to-br from-violet-50/40 via-amber-50/30 to-white p-5 mb-6"
        data-testid="branding-smart-fill"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-foreground text-background">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
              Skip the upload grind
            </div>
            <div className="font-medium text-[15px] mt-0.5">
              Set up your brand in 8 seconds
            </div>
            <div className="text-[12.5px] text-foreground/65 mt-1 leading-snug">
              Paste your website URL — we&apos;ll pull your logo, brand colors,
              favicon, and cover image automatically. No website? Generate
              clean defaults from your name + brand color.
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5 mt-4">
          <Button
            onClick={() => setImportOpen(true)}
            data-testid="bsf-import-btn"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 gap-2"
          >
            <Globe className="w-4 h-4" />
            Import from your website
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            data-testid="bsf-generate-btn"
            variant="outline"
            className="rounded-full h-11 gap-2 border-foreground/20 hover:bg-foreground/[0.04]"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4" />
                Generate from name + colors
              </>
            )}
          </Button>
        </div>
        <div className="text-[10.5px] text-foreground/45 mt-3">
          Generated/imported assets land in your branding slots below — you can
          replace any of them manually any time. We never overwrite without
          your approval.
        </div>
      </div>

      <ImportFromUrlDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApplied={() => {
          setImportOpen(false);
          onChanged?.();
        }}
      />
    </>
  );
}

// ─── Import dialog: URL paste → review → apply ──────────────────────────
function ImportFromUrlDialog({ open, onClose, onApplied }) {
  // 3-step state machine: input | review | applying
  const [step, setStep] = useState("input");
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [picks, setPicks] = useState({});  // { field: bool }
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    if (scanning || applying) return;
    setStep("input");
    setUrl("");
    setCandidates(null);
    setPicks({});
    setError("");
    onClose();
  };

  const handleScan = async () => {
    setError("");
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Paste your website URL");
      return;
    }
    setScanning(true);
    try {
      const { data } = await api.post("/seller/branding/import-from-url", {
        url: trimmed,
      });
      setCandidates(data);
      // Default — check every non-null field
      const initialPicks = {};
      for (const key of [
        "company_name", "description", "primary_color",
        "logo_url", "logo_square_url", "cover_image_url",
      ]) {
        initialPicks[key] = !!data?.[key];
      }
      setPicks(initialPicks);
      setStep("review");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setError("");
    try {
      const payload = {};
      if (picks.company_name && candidates.company_name) {
        payload.company_name = candidates.company_name;
      }
      if (picks.description && candidates.description) {
        payload.description = candidates.description;
      }
      if (picks.primary_color && candidates.primary_color) {
        payload.primary_color = candidates.primary_color;
      }
      if (picks.logo_url && candidates.logo_url) {
        payload.logo_url_source = candidates.logo_url;
      }
      if (picks.logo_square_url && candidates.logo_square_url) {
        payload.logo_square_url_source = candidates.logo_square_url;
      }
      if (picks.cover_image_url && candidates.cover_image_url) {
        payload.cover_image_url_source = candidates.cover_image_url;
      }

      if (Object.keys(payload).length === 0) {
        setError("Pick at least one field to apply.");
        setApplying(false);
        return;
      }

      const { data } = await api.post(
        "/seller/branding/apply-imported", payload,
      );
      const n = (data?.applied || []).length;
      toast.success(`Applied ${n} branding ${n === 1 ? "field" : "fields"} from ${new URL(candidates.source_url).hostname}.`);
      handleClose();
      onApplied?.();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="bsf-import-dialog"
      >
        {step === "input" && (
          <>
            <DialogHeader>
              <DialogTitle>Import branding from your website</DialogTitle>
              <DialogDescription>
                Paste the URL of your existing storefront, brand site, or
                landing page — we&apos;ll pull your logo, colors, favicon, and
                cover image automatically. Takes 5-8 seconds.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label className="text-[12px]">Website URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://glowco.com"
                data-testid="bsf-import-url"
                className="h-11"
                disabled={scanning}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleScan();
                }}
                autoFocus
              />
              <div className="text-[11.5px] text-foreground/55 leading-snug">
                We scan only the public homepage — no login, no scraping behind
                a paywall. If your site is JS-rendered or behind bot protection,
                some fields may come back empty and you&apos;ll need to add them
                manually below.
              </div>
              {error && (
                <div className="text-[12px] text-rose-700 bg-rose-50/60 rounded-lg px-3 py-2 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>{error}</div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={scanning}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleScan}
                disabled={scanning || !url.trim()}
                data-testid="bsf-import-scan"
                className="rounded-full bg-foreground text-background hover:bg-foreground/90 gap-2"
              >
                {scanning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Scanning your site…
                  </>
                ) : (
                  <>
                    Scan website
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "review" && candidates && (
          <ReviewStep
            candidates={candidates}
            picks={picks}
            setPicks={setPicks}
            applying={applying}
            error={error}
            onBack={() => { setStep("input"); setError(""); }}
            onApply={handleApply}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Review screen (checkbox per candidate field) ───────────────────────
// Helper for image-field rows: shows a small thumbnail preview.
// Hoisted above ReviewStep so React isn't recreating the component on
// every render (the no-unstable-nested-components rule).
function ImagePick({ field, label, url, hint, checked, onToggle }) {
  return (
    <Row
      checked={checked}
      onToggle={onToggle}
      label={label}
      disabled={!url}
      hint={hint}
      testid={`bsf-pick-${field}`}
    >
      {url ? (
        <img
          src={url}
          alt=""
          className="w-16 h-10 object-contain bg-foreground/[0.04] rounded border border-foreground/10"
        />
      ) : (
        <span className="text-[11px] text-foreground/45 italic">
          Not found
        </span>
      )}
    </Row>
  );
}

function ReviewStep({
  candidates, picks, setPicks, applying, error, onBack, onApply,
}) {
  const host = (() => {
    try { return new URL(candidates.source_url).hostname; } catch { return ""; }
  })();
  const togglePick = (k) => setPicks((p) => ({ ...p, [k]: !p[k] }));

  const checkedCount = Object.values(picks).filter(Boolean).length;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Review what we found</DialogTitle>
        <DialogDescription>
          From <span className="mono">{host}</span> — uncheck anything you
          want to skip, then apply.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1.5">
        {/* Company name */}
        <Row
          checked={picks.company_name}
          onToggle={() => togglePick("company_name")}
          label="Company name"
          disabled={!candidates.company_name}
          testid="bsf-pick-company_name"
        >
          <span className="text-[13px] font-medium truncate">
            {candidates.company_name || (
              <span className="text-foreground/45 italic font-normal">
                Not found
              </span>
            )}
          </span>
        </Row>

        {/* Description / tagline */}
        <Row
          checked={picks.description}
          onToggle={() => togglePick("description")}
          label="Tagline / description"
          hint="Goes into your storefront hero subtitle"
          disabled={!candidates.description}
          testid="bsf-pick-description"
        >
          <span className="text-[12px] text-foreground/75 line-clamp-2 max-w-[24rem]">
            {candidates.description || (
              <span className="italic text-foreground/45">Not found</span>
            )}
          </span>
        </Row>

        {/* Primary color */}
        <Row
          checked={picks.primary_color}
          onToggle={() => togglePick("primary_color")}
          label="Primary brand color"
          hint="From theme-color meta tag or extracted from your logo"
          disabled={!candidates.primary_color}
          testid="bsf-pick-primary_color"
        >
          {candidates.primary_color ? (
            <div className="flex items-center gap-2">
              <Palette className="w-3.5 h-3.5 text-foreground/45" />
              <span
                className="w-6 h-6 rounded-md border border-foreground/15"
                style={{ background: candidates.primary_color }}
              />
              <span className="mono text-[12px]">{candidates.primary_color}</span>
            </div>
          ) : (
            <span className="text-[11px] text-foreground/45 italic">
              Not found
            </span>
          )}
        </Row>

        <ImagePick
          field="logo_url"
          label="Primary logo (wide)"
          url={candidates.logo_url}
          hint="Used in your sidebar, invoices, emails"
          checked={picks.logo_url}
          onToggle={() => togglePick("logo_url")}
        />
        <ImagePick
          field="logo_square_url"
          label="Square / favicon"
          url={candidates.logo_square_url}
          hint="Used in browser tabs + small chips"
          checked={picks.logo_square_url}
          onToggle={() => togglePick("logo_square_url")}
        />
        <ImagePick
          field="cover_image_url"
          label="Cover image"
          url={candidates.cover_image_url}
          hint="Storefront hero background — from your og:image"
          checked={picks.cover_image_url}
          onToggle={() => togglePick("cover_image_url")}
        />
      </div>

      {/* Warnings from the scrape */}
      {candidates.warnings?.length > 0 && (
        <div className="rounded-lg bg-amber-50/50 border border-amber-200 px-3.5 py-2.5 mt-2">
          <div className="text-[11.5px] font-medium text-amber-900 flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Heads up
          </div>
          {candidates.warnings.map((w, i) => (
            <div key={i} className="text-[11.5px] text-amber-900/80 leading-snug">
              · {w}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="text-[12px] text-rose-700 bg-rose-50/60 rounded-lg px-3 py-2 mt-2">
          {error}
        </div>
      )}

      <DialogFooter className="gap-2 pt-2">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={applying}
          className="rounded-full"
          data-testid="bsf-review-back"
        >
          Back
        </Button>
        <Button
          onClick={onApply}
          disabled={applying || checkedCount === 0}
          data-testid="bsf-review-apply"
          className="rounded-full bg-foreground text-background hover:bg-foreground/90 gap-2"
        >
          {applying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Applying…
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Apply {checkedCount} field{checkedCount === 1 ? "" : "s"}
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}

// ─── Per-field row used in the review screen ─────────────────────────────
function Row({ checked, onToggle, label, hint, disabled, children, testid }) {
  return (
    <div
      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${
        disabled
          ? "border-dashed border-foreground/10 bg-foreground/[0.015]"
          : checked
          ? "border-foreground/15 bg-foreground/[0.025]"
          : "border-foreground/10 bg-white"
      }`}
      data-testid={testid}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="mt-0.5"
        data-testid={`${testid}-checkbox`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-medium">{label}</div>
        {hint && (
          <div className="text-[11px] text-foreground/55 mt-0.5">{hint}</div>
        )}
      </div>
      <div className="flex items-center shrink-0">{children}</div>
    </div>
  );
}
